import React, { useEffect, useState } from 'react';
import { Platform, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useT } from '@transifex/react';

import { AuthLayout } from '../components/AuthLayout';
import { BloomButton } from '../components/BloomButton';
import { InlineError } from '../components/InlineError';
import Loading from '../components/Loading';
import { Logo } from '../components/Logo';
import ThemedText from '../components/ThemedText';
import { ROUTES } from '../utils/constants';
import { screenTestId } from '../utils/testId';
import { sanitizeRedirectUrl } from '../utils/redirect';
import { analytics, trackSignedIn } from '../utils/analytics';
import { handleSsoCallback } from '../services/authService';
import { storeNextUrl } from '../services/storageService';
import { useLoginRouter } from '../hooks/useLoginRouter';
import { useOnAuthenticated } from '../hooks/useOnAuthenticated';

const tid = screenTestId('SsoCallback');

export default function SSOCallbackScreen() {
  const router = useLoginRouter();
  const params = useLocalSearchParams();
  const t = useT();
  const onAuthenticated = useOnAuthenticated();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = typeof params.code === 'string' ? params.code : undefined;
    let cancelled = false;

    (async () => {
      if (!code) {
        analytics.trackEvent('sso_callback_missing_code');
        router.replace(ROUTES.LOGIN);
        return;
      }

      try {
        analytics.trackEvent('sso_callback_started');
        const { token, nextApp, destination } = await handleSsoCallback(code);
        if (cancelled) return;

        const safe = sanitizeRedirectUrl(destination, ROUTES.LOGIN);
        analytics.trackEvent('sso_callback_succeeded', { destination: safe });

        // `destination` is the authoritative post-SSO landing URL:
        // handleSsoCallback resolves it from `result.relay_state` when the
        // SAML round-trip carried a RelayState (Okta tile Default RelayState,
        // or a `?next=` on the sso-login screen that got forwarded through
        // the AuthnRequest), falling back to `resolveDestination(next_app)`
        // otherwise. We navigate to it directly rather than round-tripping
        // through `completeAuth` → `redirectAfterAuth`, which would drop the
        // relay_state signal and always resolve from `next_app` alone
        // (landing role-specific SSO users on /app/ instead of their
        // intended tile destination).
        //
        // In native mode this screen isn't mounted — sso-login.tsx
        // handleSubmit intercepts the `vidahealthsso://` scheme redirect
        // and runs the exchange inline. Consumer-callback path is still
        // honored here for safety in case someone routes to /sso-callback
        // on native (e.g. deep link testing).
        if (onAuthenticated) {
          await onAuthenticated({ token, nextApp });
          trackSignedIn('sso', token, nextApp);
        } else if (Platform.OS === 'web') {
          await storeNextUrl(safe);
          trackSignedIn('sso', token, nextApp);
          await analytics.flush();
          window.location.href = safe;
        } else {
          throw new Error(
            'SSO callback screen mounted on native without an onAuthenticated callback'
          );
        }
      } catch {
        if (cancelled) return;
        analytics.trackEvent('sso_callback_failed');
        setError(t('Sign-in failed. Please try again.'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleBackToLogin = () => {
    router.replace(ROUTES.LOGIN);
  };

  return (
    <AuthLayout>
      <View className='w-full'>
        <Logo />
        <View className='items-center justify-center py-16' testID={tid('container')}>
          {error ? (
            <>
              <InlineError message={error} testID={tid('error')} />
              <View className='mt-6 w-full'>
                <BloomButton
                  text={t('Back to sign in')}
                  onPress={handleBackToLogin}
                  testID={tid('back-button')}
                />
              </View>
            </>
          ) : (
            <>
              <Loading className='flex-none' />
              <ThemedText type='bodyRegular' color='subtle' className='mt-4'>
                {t('Signing you in...')}
              </ThemedText>
            </>
          )}
        </View>
      </View>
    </AuthLayout>
  );
}
